/**
 * HotelFiltersSidebar — left-rail filters for /hotels/results.
 *
 * All filter ranges & option lists are derived from the LIVE hotel
 * response data (not hardcoded), so filters always reflect the user's
 * actual search context.
 */
import React, { useMemo } from 'react';
import { Star, BadgeCheck, Search, Sparkles, X, MapPin } from 'lucide-react';

const Section = ({ title, children, testId }) => (
  <div className="py-3 border-b border-gray-100 last:border-b-0" data-testid={testId}>
    <h4 className="text-[11px] uppercase tracking-wider font-bold text-[#1B375B] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {title}
    </h4>
    {children}
  </div>
);

const Checkbox = ({ checked, onChange, label, count, testId }) => (
  <label
    className="flex items-center gap-2 py-1 cursor-pointer text-xs text-gray-700 hover:text-[#1B375B] group"
    data-testid={testId}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-3.5 h-3.5 rounded border-gray-300 text-[#1B375B] focus:ring-[#659A9B]"
    />
    <span className="flex-1 truncate">{label}</span>
    {count !== undefined && (
      <span className="text-[10px] text-gray-400 font-semibold tabular-nums">{count}</span>
    )}
  </label>
);

// Extract a locality / sub-area string from a Tripjack address object.
// Tries `adr2` first (cleanest signal), falls back to a heuristic on `adr`.
// Returns "" when no reliable value found.
export const _localityOf = (ad) => {
  if (!ad || typeof ad !== 'object') return '';
  const city = ((ad.ctn || ad.city?.name) || '').trim().toLowerCase();
  const adr2 = (ad.adr2 || '').trim();
  if (adr2) {
    // adr2 often has both locality + city ("Marol, Andheri (E)" or "Bandra West, Mumbai")
    const parts = adr2.split(',').map((p) => p.trim()).filter(Boolean);
    const nonCity = parts.find((p) => p.toLowerCase() !== city) || parts[parts.length - 1] || '';
    return nonCity;
  }
  // Fallback: parse adr for a trailing area name (only when it looks like a place name)
  const adr = (ad.adr || '').trim();
  if (!adr) return '';
  const tail = adr.split(',').map((p) => p.trim()).filter(Boolean).pop();
  if (tail && tail.length <= 30 && !/^\d/.test(tail) && tail.toLowerCase() !== city) {
    return tail;
  }
  return '';
};

export const _cityOf = (ad) => {
  if (!ad || typeof ad !== 'object') return '';
  return (ad.ctn || ad.city?.name || '').trim();
};

const HotelFiltersSidebar = ({ hotels, filters, setFilters, onReset, total }) => {
  const facets = useMemo(() => {
    const prices = hotels.map((h) => h.starting_price).filter((p) => typeof p === 'number' && p > 0);
    const minPrice = prices.length ? Math.floor(Math.min(...prices)) : 0;
    const maxPrice = prices.length ? Math.ceil(Math.max(...prices)) : 0;

    const ratingCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let refundableCount = 0;
    const amenityCounts = {};
    const cityCounts = {};
    const localityCounts = {};
    for (const h of hotels) {
      const r = Math.round(h.rating || 0);
      if (r >= 1 && r <= 5) ratingCounts[r]++;
      if (h.refundable === true) refundableCount++;
      for (const a of (h.amenities || [])) {
        const key = String(a).trim();
        if (!key) continue;
        amenityCounts[key] = (amenityCounts[key] || 0) + 1;
      }
      const city = _cityOf(h.address);
      if (city) cityCounts[city] = (cityCounts[city] || 0) + 1;
      const loc = _localityOf(h.address);
      if (loc) localityCounts[loc] = (localityCounts[loc] || 0) + 1;
    }
    const topAmenities = Object.entries(amenityCounts).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, count]) => ({ name, count }));
    const topCities = Object.entries(cityCounts).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, count]) => ({ name, count }));
    const topLocalities = Object.entries(localityCounts).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, count]) => ({ name, count }));

    return { minPrice, maxPrice, ratingCounts, refundableCount, topAmenities, topCities, topLocalities };
  }, [hotels]);

  const toggleStar = (s) => {
    const next = new Set(filters.stars);
    next.has(s) ? next.delete(s) : next.add(s);
    setFilters((f) => ({ ...f, stars: Array.from(next) }));
  };
  const toggleAmenity = (a) => {
    const next = new Set(filters.amenities);
    next.has(a) ? next.delete(a) : next.add(a);
    setFilters((f) => ({ ...f, amenities: Array.from(next) }));
  };
  const toggleCity = (c) => {
    const next = new Set(filters.cities || []);
    next.has(c) ? next.delete(c) : next.add(c);
    setFilters((f) => ({ ...f, cities: Array.from(next) }));
  };
  const toggleLocality = (l) => {
    const next = new Set(filters.localities || []);
    next.has(l) ? next.delete(l) : next.add(l);
    setFilters((f) => ({ ...f, localities: Array.from(next) }));
  };

  const activeCount =
    (filters.stars.length || 0) +
    (filters.amenities.length || 0) +
    ((filters.cities || []).length || 0) +
    ((filters.localities || []).length || 0) +
    (filters.refundableOnly ? 1 : 0) +
    (filters.query ? 1 : 0) +
    (filters.priceMax && filters.priceMax < facets.maxPrice ? 1 : 0) +
    (filters.priceMin && filters.priceMin > facets.minPrice ? 1 : 0);

  return (
    <aside
      className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sticky lg:top-4 self-start"
      data-testid="hotel-filters-sidebar"
    >
      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#659A9B]" />
          <h3 className="text-sm font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Filters
          </h3>
          {activeCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#1B375B] text-white text-[9px] font-bold tabular-nums">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="text-[10px] font-bold text-[#659A9B] hover:underline"
            data-testid="hotel-filters-reset"
          >
            Reset
          </button>
        )}
      </div>

      <Section title="Hotel Name" testId="hotel-filter-section-name">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="e.g. Taj, Marriott"
            value={filters.query}
            onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#659A9B]"
            data-testid="hotel-filter-name-input"
          />
          {filters.query && (
            <button
              type="button"
              onClick={() => setFilters((f) => ({ ...f, query: '' }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid="hotel-filter-name-clear"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </Section>

      {/* City — auto-hidden when ≤1 distinct city */}
      {facets.topCities.length > 1 && (
        <Section title="City" testId="hotel-filter-section-city">
          <div className="max-h-44 overflow-y-auto pr-1 -mr-1">
            {facets.topCities.map((c) => (
              <Checkbox
                key={c.name}
                checked={(filters.cities || []).includes(c.name)}
                onChange={() => toggleCity(c.name)}
                label={
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-[#659A9B]" />
                    {c.name}
                  </span>
                }
                count={c.count}
                testId={`hotel-filter-city-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Locality / Area — derived from adr2 with adr fallback */}
      {facets.topLocalities.length > 0 && (
        <Section title="Locality / Area" testId="hotel-filter-section-locality">
          <div className="max-h-44 overflow-y-auto pr-1 -mr-1">
            {facets.topLocalities.map((l) => (
              <Checkbox
                key={l.name}
                checked={(filters.localities || []).includes(l.name)}
                onChange={() => toggleLocality(l.name)}
                label={l.name}
                count={l.count}
                testId={`hotel-filter-locality-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              />
            ))}
          </div>
        </Section>
      )}

      {facets.maxPrice > 0 && (
        <Section title={`Price · ₹${facets.minPrice.toLocaleString('en-IN')} – ₹${facets.maxPrice.toLocaleString('en-IN')}`} testId="hotel-filter-section-price">
          <div className="text-[10px] text-gray-500 mb-1 flex items-center justify-between">
            <span className="font-bold tabular-nums" data-testid="hotel-filter-price-min-label">₹{(filters.priceMin ?? facets.minPrice).toLocaleString('en-IN')}</span>
            <span className="font-bold tabular-nums" data-testid="hotel-filter-price-max-label">₹{(filters.priceMax ?? facets.maxPrice).toLocaleString('en-IN')}</span>
          </div>
          <input
            type="range"
            min={facets.minPrice}
            max={facets.maxPrice}
            step={Math.max(1, Math.floor((facets.maxPrice - facets.minPrice) / 50))}
            value={filters.priceMax ?? facets.maxPrice}
            onChange={(e) => setFilters((f) => ({ ...f, priceMax: parseInt(e.target.value, 10) }))}
            className="w-full accent-[#1B375B]"
            data-testid="hotel-filter-price-range"
          />
          <p className="text-[10px] text-gray-400 mt-1">Showing hotels up to selected price per night</p>
        </Section>
      )}

      <Section title="Star Rating" testId="hotel-filter-section-stars">
        {[5, 4, 3, 2, 1].map((s) => (
          <Checkbox
            key={s}
            checked={filters.stars.includes(s)}
            onChange={() => toggleStar(s)}
            count={facets.ratingCounts[s] || 0}
            label={
              <span className="flex items-center gap-0.5">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    className={`w-3 h-3 ${i < s ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                  />
                ))}
              </span>
            }
            testId={`hotel-filter-star-${s}`}
          />
        ))}
      </Section>

      <Section title="Cancellation" testId="hotel-filter-section-cancellation">
        <Checkbox
          checked={filters.refundableOnly}
          onChange={() => setFilters((f) => ({ ...f, refundableOnly: !f.refundableOnly }))}
          label={
            <span className="flex items-center gap-1.5">
              <BadgeCheck className="w-3 h-3 text-emerald-500" /> Free Cancellation
            </span>
          }
          count={facets.refundableCount}
          testId="hotel-filter-refundable"
        />
      </Section>

      {facets.topAmenities.length > 0 && (
        <Section title="Amenities" testId="hotel-filter-section-amenities">
          {facets.topAmenities.map((a) => (
            <Checkbox
              key={a.name}
              checked={filters.amenities.includes(a.name)}
              onChange={() => toggleAmenity(a.name)}
              label={a.name}
              count={a.count}
              testId={`hotel-filter-amenity-${a.name.toLowerCase().replace(/\s+/g, '-')}`}
            />
          ))}
        </Section>
      )}

      <p className="text-[10px] text-gray-400 mt-2" data-testid="hotel-filters-summary">
        Showing <span className="font-bold text-[#1B375B] tabular-nums">{total}</span> hotel{total === 1 ? '' : 's'}
      </p>
    </aside>
  );
};

export default HotelFiltersSidebar;
